/**
 * Krok 5: Kvalitativní faktory, v13
 * Segment je nyní v KPIs (Step2). Zde zůstává: product mix, fulfillment,
 * founder_dependency, product_concentration_pct, single_supplier_revenue_pct,
 * market_entry_barriers, product_consumability, business_age_years
 */
import React, { useState } from 'react';
import { TooltipLabel } from './TooltipLabel';
import { posunNaChybu } from '../utils/chyby';

function RadioGroup({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            padding: '0.5rem 1rem',
            border: `2px solid ${value === opt.value ? 'var(--purple-bright)' : 'var(--purple-mid)'}`,
            borderRadius: '8px',
            background: value === opt.value ? 'rgba(155, 89, 212, 0.12)' : 'var(--purple-dark)',
            color: value === opt.value ? 'var(--purple-bright)' : 'var(--star-white)',
            fontWeight: value === opt.value ? 700 : 400,
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function Step4_QualitativeData({ wizardState, setWizardState, onNext, onBack }) {
  const [form, setForm] = useState({ ...wizardState.qualitative });
  const [errors, setErrors] = useState({});

  // Otázky podle typu podnikání. Produktový mix, dodavatelé a portfolio
  // patří ke zboží, logistika a spotřebnost k e-shopu a obchodu. Služby
  // (ubytování, poradenství) se na ně neptáme, výpočet je u nich nehodnotí.
  const typ = wizardState.typ_podnikani || 'eshop';
  const produktovy = ['eshop', 'obchod', 'vyroba'].includes(typ);
  const jeEshop = typ === 'eshop';
  const spotrebni = ['eshop', 'obchod'].includes(typ);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    // Odpovědi s dopadem na hodnotu nejsou předvyplněné. Dřív kdo krok jen
    // proklikl, byl „přeprodejce se střední závislostí na zakladateli“ a
    // dostal za to srážku (plán v2, Z5).
    const e = {};
    if (!form.founder_dependency) e.founder_dependency = 'Vyberte prosím jednu možnost.';
    if (!form.market_entry_barriers) e.market_entry_barriers = 'Vyberte prosím jednu možnost.';
    if (jeEshop && !form.fulfillment) e.fulfillment = 'Vyberte prosím jednu možnost.';
    if (spotrebni && !form.product_consumability) e.product_consumability = 'Vyberte prosím jednu možnost.';
    if (typ === 'vyroba' && Number(form.resale_pct) >= 100)
      e.product_mix = 'U výroby vyplňte podíl vlastní výroby nebo vlastní značky.';
    if (!jeEshop && (form.customer_concentration_pct === '' || form.customer_concentration_pct == null))
      e.customer_concentration_pct = 'Vyplňte podíl tří největších odběratelů na tržbách.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validate()) { posunNaChybu(); return; }
    setWizardState(prev => ({ ...prev, qualitative: { ...form } }));
    onNext();
  };

  return (
    <div className="step-container">
      <h2>Kvalitativní faktory</h2>
      <p className="step-description">
        Pár informací o tom, jak firma funguje. Vyberte u každé možnost, která vás nejlíp vystihuje.
      </p>

      <div className="edit-form">

        {produktovy && (<>
        {/* Product Mix, 4 jasně oddělené kategorie, auto-dopočet přeprodeje do 100 % */}
        <div className="form-group">
          <label style={{ fontWeight: 600 }}>Produktový mix</label>
          {errors.product_mix && <small className="chyba-pole" style={{ display: 'block' }}>{errors.product_mix}</small>}
          <small style={{ display: 'block', color: 'var(--text-dim)', margin: '0.2rem 0 0.6rem' }}>
            Jaký podíl tržeb pochází z jakého původu zboží? Vyplňte, co se vás týká. Přeprodej cizích
            značek se dopočítá automaticky do 100 %.
          </small>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[
              { key: 'own_production_pct', label: 'Vlastní výroba', hint: 'Produkty fyzicky vyrábíte vy (vlastní výroba, dílna, manufaktura).' },
              { key: 'own_brand_pct', label: 'Vlastní značka', hint: 'Vaše originální značka (návrh, design), výrobu pro vás zajišťuje externí výrobce.' },
              { key: 'private_label_pct', label: 'Private label / white label', hint: 'Hotový produkt od výrobce, na který dáte své logo nebo etiketu.' },
            ].map(({ key, label, hint }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ minWidth: '220px' }}>
                  <span style={{ fontSize: '0.9rem' }}>{label}</span><br />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{hint}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <input
                    type="number" min="0" max="100"
                    value={form[key]}
                    onChange={e => {
                      const raw = Math.max(0, Number(e.target.value) || 0);
                      setForm(prev => {
                        const editKeys = ['own_production_pct', 'own_brand_pct', 'private_label_pct'];
                        const others = editKeys
                          .filter(k => k !== key)
                          .reduce((sum, k) => sum + (Number(prev[k]) || 0), 0);
                        const val = Math.min(raw, 100 - others);
                        return { ...prev, [key]: val, resale_pct: Math.max(0, 100 - val - others) };
                      });
                    }}
                    style={{ width: '70px', textAlign: 'center' }}
                  />
                  <span style={{ fontWeight: 600 }}>%</span>
                </div>
              </div>
            ))}
            {/* Přeprodej, dopočítaný */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ minWidth: '220px' }}>
                <span style={{ fontSize: '0.9rem' }}>Přeprodej cizích značek / dropshipping</span><br />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Cizí značky, které jen nakupujete a přeprodáváte. Dopočítá se automaticky.</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <input type="number" value={form.resale_pct} readOnly disabled
                  style={{ width: '70px', textAlign: 'center', background: 'var(--purple-deep)', color: 'var(--star-white)' }} />
                <span style={{ fontWeight: 600 }}>%</span>
              </div>
            </div>
          </div>
        </div>
        </>)}

        {jeEshop && (<>
        {/* Fulfillment */}
        <div className="form-group">
          <TooltipLabel
            label="Logistika (fulfillment)"
            tooltip="Jak řešíte skladování a expedici? Vlastní sklad = zboží skladujete a odbavujete sami. Externalizovaná logistika (3PL) = expedici za vás zajišťuje externí firma. Dropshipping = zboží posílá přímo dodavatel, vy ho neskladujete."
          />
          <RadioGroup
            value={form.fulfillment}
            onChange={v => update('fulfillment', v)}
            options={[
              { value: 'own_warehouse', label: 'Vlastní sklad' },
              { value: 'third_party',   label: 'Externalizovaná 3PL' },
              { value: 'dropshipping',  label: 'Dropshipping' },
            ]}
          />
          {errors.fulfillment && <small className="chyba-pole" style={{ display: 'block' }}>{errors.fulfillment}</small>}
        </div>
        </>)}

        {/* Founder Dependency */}
        <div className="form-group">
          <TooltipLabel
            label="Závislost byznysu na zakladateli"
            tooltip="Jak moc je chod firmy závislý na vás jako zakladateli, tedy zda firma běží i bez vašeho každodenního zapojení, nebo na vás stojí klíčové know-how a vztahy se zákazníky a dodavateli."
          />
          <RadioGroup
            value={form.founder_dependency}
            onChange={v => update('founder_dependency', v)}
            options={[
              { value: 'low',    label: 'Nízká, byznys funguje bez majitele' },
              { value: 'medium', label: 'Střední, standardní závislost' },
              { value: 'high',   label: 'Vysoká, majitel zajišťuje vše klíčové' },
            ]}
          />
          {errors.founder_dependency && <small className="chyba-pole" style={{ display: 'block' }}>{errors.founder_dependency}</small>}
        </div>

        {produktovy ? (<>
        {/* Product Concentration */}
        <div className="form-group">
          <TooltipLabel
            label="Produktové portfolio"
            tooltip="Kolik produktů/SKU máte v nabídce a kolik procent tržeb pochází z top 3? Posuzuje se podle velikosti portfolia: u 5 produktů je vysoký podíl top 3 přirozený, u 500 produktů je spíš neobvyklý."
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Počet produktů/SKU</label>
              <input
                type="number"
                value={form.total_sku_count}
                onChange={e => update('total_sku_count', e.target.value)}
                placeholder="např. 150"
                min="1"
                style={{ maxWidth: '120px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>% tržeb z top 3 produktů</label>
              <input
                type="number"
                value={form.product_concentration_pct}
                onChange={e => update('product_concentration_pct', Number(e.target.value))}
                min="0" max="100"
                style={{ maxWidth: '100px' }}
              />
            </div>
          </div>
        </div>
        </>) : (<>
        {/* Odběratelé u služeb a ostatních: viz společná otázka níže */}
        </>)}

        {produktovy && (<>
        {/* Supplier Dependency */}
        <div className="form-group">
          <TooltipLabel
            label="Dodavatelská závislost"
            tooltip="Kolik máte dodavatelů celkem a jaký podíl tvoří ten největší? Pokud máte jednoho dodavatele a ten tvoří 100%, to je jiná situace než když máte 20 dodavatelů a hlavní tvoří 30%."
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>Počet dodavatelů</label>
              <input
                type="number"
                value={form.total_supplier_count}
                onChange={e => update('total_supplier_count', e.target.value)}
                placeholder="např. 5"
                min="1"
                style={{ maxWidth: '100px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>% od hlavního dodavatele</label>
              <input
                type="number"
                value={form.single_supplier_revenue_pct}
                onChange={e => update('single_supplier_revenue_pct', Math.min(100, Math.max(0, Number(e.target.value))))}
                min="0" max="100"
                style={{ maxWidth: '100px' }}
              />
            </div>
          </div>
        </div>
        </>)}

        {/* Odběratelé: u všech typů. Výrobce blatníků s jedním automobilkou na
            75 % tržeb dřív neměl kam to zadat (plán v2, Z3). U prodeje koncovým
            zákazníkům je volitelná. */}
        <div className="form-group">
          <TooltipLabel
            label={jeEshop ? 'Závislost na největších odběratelích (volitelné)' : 'Závislost na největších odběratelích *'}
            tooltip="Kolik procent tržeb pochází od tří největších odběratelů nebo klientů. Čím vyšší podíl, tím víc by firmu poškodil odchod jednoho z nich, a kupující to promítne do ceny."
          />
          <div>
            <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.25rem' }}>% tržeb od 3 největších odběratelů</label>
            <input
              type="number"
              value={form.customer_concentration_pct ?? ''}
              onChange={e => update('customer_concentration_pct', e.target.value === '' ? '' : Math.min(100, Math.max(0, Number(e.target.value))))}
              min="0" max="100"
              style={{ maxWidth: '100px' }}
              className={errors.customer_concentration_pct ? 'chyba-pole' : undefined}
            />
          </div>
          {errors.customer_concentration_pct && <small className="chyba-pole" style={{ display: 'block' }}>{errors.customer_concentration_pct}</small>}
        </div>

        {/* Bariéry vstupu na trh */}
        <div className="form-group">
          <TooltipLabel
            label="Bariéry vstupu na trh"
            tooltip="Jak těžké je pro novou konkurenci vstoupit na váš trh? Vysoké bariéry = regulace, patenty, silná značka nebo exkluzivní smlouvy. Nízké bariéry = kdokoli může poměrně snadno začít konkurovat."
          />
          <RadioGroup
            value={form.market_entry_barriers}
            onChange={v => update('market_entry_barriers', v)}
            options={[
              { value: 'low',    label: 'Nízké, snadný vstup konkurence' },
              { value: 'medium', label: 'Střední, vyžaduje know-how nebo kapitál' },
              { value: 'high',   label: 'Vysoké, regulace, patenty, silná značka' },
            ]}
          />
          {errors.market_entry_barriers && <small className="chyba-pole" style={{ display: 'block' }}>{errors.market_entry_barriers}</small>}
        </div>

        {spotrebni && (<>
        {/* Spotřebnost produktu */}
        <div className="form-group">
          <TooltipLabel
            label="Spotřebnost produktu"
            tooltip="Jak rychle zákazníci spotřebují váš produkt a potřebují nakoupit znovu? Spotřební zboží (vitamíny, kosmetika, jídlo) se nakupuje opakovaně. Trvanlivé produkty (nábytek, elektronika) mají delší nákupní cyklus."
          />
          <RadioGroup
            value={form.product_consumability}
            onChange={v => update('product_consumability', v)}
            options={[
              { value: 'consumable',      label: 'Spotřební, vitamíny, kosmetika, jídlo' },
              { value: 'semi_consumable',  label: 'Polospotřební, oblečení, doplňky' },
              { value: 'durable',          label: 'Trvanlivé, nábytek, elektronika' },
            ]}
          />
          {errors.product_consumability && <small className="chyba-pole" style={{ display: 'block' }}>{errors.product_consumability}</small>}
        </div>
        </>)}

        {/* Věk byznysu */}
        <div className="form-group">
          <TooltipLabel
            label="Jak dlouho podnik funguje?"
            tooltip="Jak dlouho už podnik funguje, počítáno od zahájení činnosti."
          />
          <span style={{ fontWeight: 400, fontSize: '0.85rem', color: 'var(--text-dim)' }}>(volitelné)</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.35rem' }}>
            <input
              type="number"
              value={form.business_age_years}
              onChange={e => update('business_age_years', e.target.value)}
              min="0.1"
              step="0.5"
              placeholder="např. 3.5"
              style={{ maxWidth: '120px' }}
            />
            <span>let</span>
          </div>
          <small style={{ color: 'var(--text-dim)' }}>
            {form.business_age_years === '' && 'Nechte prázdné, použijeme neutrální hodnotu'}
          </small>
        </div>

      </div>

      <div className="button-group">
        <button onClick={onBack} className="btn-secondary">Zpět</button>
        <button onClick={handleNext} className="btn-primary">Spočítat valuaci</button>
      </div>
    </div>
  );
}
